/**
 * Horse Management Types
 */

export interface Horse {
  id: string;
  name: string;
  game_chukkers: number; // Max chukkers a horse can play per game
  color: string;
  sex: HorseSex;
  birthdate?: string | null; // 'YYYY-MM-DD' or null when unknown
  owner_id: string | null; // null = club owned, otherwise player_id
  owner_type: "club" | "player";
  status: "available" | "unavailable";
  notes: string;
  created_at: string;
  updated_at: string;
  // Extended fields from API when checking availability
  remaining_capacity?: number;
  current_usage?: number;
}

export type HorseSex = "mare" | "gelding" | "stallion";

export interface HorseAssignment {
  id: string;
  horse_id: string;
  horse_name: string;
  horse_color?: string; // Included in API response
  player_id: string;
  chukkers_assigned: number;
  chukker_numbers: number[];
  assigned_at: string;
}

export interface HorseRequest {
  id: string;
  player_id: string;
  chukkers_requested: number;
  status: "pending" | "fulfilled" | "cancelled";
  requested_at: string;
  updated_at?: string;
}

// Selectable horse colors going forward (realistic equine coats).
// Roan is stylized as light purple and Palomino as gold per design preference.
export const HORSE_COLORS = [
  "Gray",
  "Black",
  "Chestnut",
  "Bay",
  "Dark Bay",
  "Paint",
  "Palomino",
  "Roan",
  "Dun",
] as const;

export type HorseColor = (typeof HORSE_COLORS)[number];

// Color palettes (face, muzzle, mane) for the new equine coats.
const NEW_HORSE_COLOR_HEX: Record<HorseColor, { face: string; muzzle: string; mane: string }> = {
  Gray: { face: "#9E9E9E", muzzle: "#BDBDBD", mane: "#757575" },
  Black: { face: "#1C1C1C", muzzle: "#333333", mane: "#000000" },
  Chestnut: { face: "#A0522D", muzzle: "#C68642", mane: "#6B3A1E" },
  Bay: { face: "#8B4513", muzzle: "#A0623A", mane: "#1C1C1C" },
  "Dark Bay": { face: "#4E342E", muzzle: "#5D4037", mane: "#1A1A1A" },
  Paint: { face: "#6B4226", muzzle: "#F0EDE5", mane: "#FFFFFF" },
  Palomino: { face: "#DAA520", muzzle: "#E8C547", mane: "#F5ECD7" },
  Roan: { face: "#B7A3C3", muzzle: "#D7C7E0", mane: "#8E7BA6" },
  Dun: { face: "#C2A878", muzzle: "#D4C19C", mane: "#5A4327" },
};

// Legacy colors kept only for backwards-compatible display of existing horses.
// These are intentionally NOT offered as options for new/edited horses.
const LEGACY_HORSE_COLOR_HEX: Record<string, { face: string; muzzle: string; mane: string }> = {
  Brown: { face: "#8B5A2B", muzzle: "#C68642", mane: "#5A3A1B" },
  DarkBrown: { face: "#5C4033", muzzle: "#8D6E63", mane: "#3E2723" },
  White: { face: "#F5F5F5", muzzle: "#E0E0E0", mane: "#CCCCCC" },
  Golden: { face: "#DAA520", muzzle: "#E8C547", mane: "#B8860B" },
  Tan: { face: "#D2B48C", muzzle: "#E5D4B8", mane: "#A08060" },
  Pink: { face: "#F4A6C1", muzzle: "#FBCFE8", mane: "#DB7093" },
  Purple: { face: "#9B59B6", muzzle: "#BB8FCE", mane: "#7D3C98" },
  Blue: { face: "#5DADE2", muzzle: "#85C1E9", mane: "#2980B9" },
  Mint: { face: "#82E0AA", muzzle: "#ABEBC6", mane: "#58D68D" },
  Teal: { face: "#48C9B0", muzzle: "#76D7C4", mane: "#1ABC9C" },
  Red: { face: "#E74C3C", muzzle: "#F1948A", mane: "#C0392B" },
  Orange: { face: "#F39C12", muzzle: "#F8C471", mane: "#D68910" },
  Yellow: { face: "#F7DC6F", muzzle: "#FCF3CF", mane: "#D4AC0D" },
  Navy: { face: "#34495E", muzzle: "#5D6D7E", mane: "#1C2833" },
  Sand: { face: "#EAD7B7", muzzle: "#F5ECD7", mane: "#C9B896" },
  Charcoal: { face: "#424949", muzzle: "#5F6A6A", mane: "#1C2020" },
  Lavender: { face: "#D7BDE2", muzzle: "#EBDEF0", mane: "#AF7AC5" },
};

// Combined lookup used for display/icon rendering. Includes both the current
// selectable colors and legacy colors so existing horses keep their appearance.
export const HORSE_COLOR_HEX: Record<string, { face: string; muzzle: string; mane: string }> = {
  ...LEGACY_HORSE_COLOR_HEX,
  ...NEW_HORSE_COLOR_HEX,
};

export interface HorseFormData {
  name: string;
  game_chukkers: number;
  color: HorseColor;
  sex: HorseSex;
  birthdate: string | null; // 'YYYY-MM-DD' or null when unknown
  owner_id: string | null;
  owner_type: "club" | "player";
  status: "available" | "unavailable";
  notes: string;
}

export interface Player {
  player_id: string | number;
  first_name: string;
  last_name: string;
}

